import React, { useCallback, useState, useRef, useEffect, useMemo, memo } from 'react';
import uuid from 'uuid-random';
import { useTranslations } from 'js/hooks/i18n';
import { clsb, clsx } from 'js/utils/classNames.util';
import { useEffectOnce } from 'js/hooks/useEffectOnce';
import { componentService } from 'js/services/component.service';

import * as Types from './types';
import * as helpers from './helpers';
import { Label } from '../Label';
import { withFinalFormField } from '../../hocs';
import { ClearInputIcon } from '../ClearInputIcon';
import { FieldBottomMessage } from '../FieldBottomMessage';
import { preventDefault, getInteractionClass } from '../../helpers';
import { AutocompleteOptions } from './components/AutocompleteOptions';
import { VISIBLE_ITEMS_COUNT, MIN_SYMBOLS_TO_SEARCH } from './constants';

import '../../FormFields.scss';
import style from './Autocomplete.scss';

export const AutocompleteBare: React.FC<Types.IAutocompleteProps> = (props) => {
    const {
        /* rff render props */
        onBlur, onFocus, onChange,
        meta = {},

        'aria-label': ariaLabel,
        className,
        classNames,
        disabled,
        error: customError,
        hint,
        id: propsId,
        infoTooltip,
        isLabelAsteriskEnabled,
        isPlaceholderAsteriskEnabled,
        label,
        minSymbolsToSearch = MIN_SYMBOLS_TO_SEARCH,
        name,
        noOptionsMessage,
        onMouseDown,
        onOptionSelect,
        onSearchChange,
        Option,
        options: propsOptions,
        placeholder,
        readOnly,
        value,
        visibleOptionsCount = VISIBLE_ITEMS_COUNT
    } = props;
    const translate = useTranslations();
    const idRef = useRef<string>(propsId || uuid());
    const id = idRef.current;
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const valueInputRef = useRef<HTMLInputElement>(null);
    const optionsContainerRef = useRef<HTMLDivElement>(null);

    const options = useMemo((): Types.IAutocompleteOption[] => helpers.parseOptions(propsOptions), [propsOptions]);
    const initialOption = useMemo((): Types.IAutocompleteOption | null => options
        .find(option => option.value === value) || null,
        [value, options]
    );
    const [showClearInputIcon, setShowClearInputIcon] = useState<boolean>(false);
    const [chosenOption, setChosenOption] = useState<Types.IAutocompleteOption | null>(initialOption);
    const [isListOpened, setIsListOpened] = useState<boolean>(false);
    const [list, setList] = useState<Types.IAutocompleteOption[]>(options);
    const [activeValue, setActiveValue] = useState<string>();
    const isDisabled = disabled || readOnly || meta.submitting || meta.validating;

    const interactionClass = getInteractionClass({
        readOnly,
        error: customError || meta?.error,
        disabled: isDisabled,
        active: isListOpened ? true : meta?.active,
        touched: meta?.touched,
        submitting: meta?.submitting
    });

    // region input field processing
    useEffectOnce(() => {
        if (!valueInputRef.current || !inputRef.current || !initialOption) {
            return;
        }

        inputRef.current.value = initialOption.label;
        valueInputRef.current.value = initialOption.value;
        setShowClearInputIcon(true);
    }, [initialOption]);

    const onValueChange = useCallback((nextValue: string): void => {
        setShowClearInputIcon(Boolean(nextValue));

        onChange?.({ target: { ...valueInputRef.current, value: nextValue } } as React.ChangeEvent<HTMLInputElement>);

        if (valueInputRef.current) {
            valueInputRef.current.value = nextValue;
        }
    }, [onChange]);

    const onClearInputIconClick = useCallback((event: React.MouseEvent<HTMLElement>): void => {
        event.stopPropagation();
        setIsListOpened(false);

        if (!inputRef.current) {
            return;
        }

        inputRef.current.value = '';
        inputRef.current.focus();

        onValueChange('');
        setChosenOption(null);
        minSymbolsToSearch > 0 && setIsListOpened(false);
    }, [minSymbolsToSearch, onValueChange]);

    const onInputFocus = useCallback((event: React.FocusEvent<HTMLInputElement>): void => {
        onFocus?.(event);

        if (inputRef.current?.value) {
            setIsListOpened(true);
        }
    }, [onFocus]);

    const onInputChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const nextValue = event.target.value;

        setShowClearInputIcon(Boolean(nextValue));

        if (nextValue.length < minSymbolsToSearch) {
            setIsListOpened(false);
            return;
        }

        if (onSearchChange) {
            const nextList = await onSearchChange(nextValue);

            if (!nextList) {
                setIsListOpened(false);
                return;
            }

            setIsListOpened(true);
            setList(nextList);
        } else {
            setIsListOpened(true);
            setList(options.filter(item => item.label.toLowerCase().includes(nextValue.toLowerCase())));
        }

        optionsContainerRef.current?.scrollTo(0, 0);
    }, [options, onSearchChange, minSymbolsToSearch]);

    // endregion

    // region options processing
    const handleCloseList = useCallback((currentOption?: Types.IAutocompleteOption): void => {
        setIsListOpened(false);
        setActiveValue(undefined);
        setList(options);

        const inputValue = currentOption?.label || chosenOption?.label || '';

        if (!inputRef.current) {
            return;
        }

        inputRef.current.value = inputValue;

        if (!inputValue) {
            onValueChange('');
            setChosenOption(null);
        }

        inputRef.current.blur();
        onBlur?.({ target: inputRef.current } as React.FocusEvent<HTMLInputElement>);
    }, [onValueChange, onBlur, options, chosenOption]);

    const onOptionClick = useCallback(async (option: Types.IAutocompleteOption): Promise<void> => {
        const handleOptionChange = () => {
            onValueChange(option.value);
            setChosenOption(option);
            handleCloseList(option);
        };

        if (!onOptionSelect) {
            handleOptionChange();

            return;
        }

        const newOptions = await onOptionSelect(option);

        if (newOptions?.length) {
            setList(newOptions);
        } else {
            handleOptionChange();
        }
    }, [onOptionSelect, onValueChange, handleCloseList]);

    // endregion

    // region events processing
    const handleDocumentKeyDown = useCallback((event: KeyboardEvent): void => {
        if (isListOpened && event.key === 'Tab' && !containerRef.current?.contains(document.activeElement as Node)) {
            handleCloseList();

            return;
        }

        if (event.key === 'Escape' && inputRef.current && containerRef.current?.contains(event.target as Node)) {
            inputRef.current.value = '';
            onValueChange('');
            setChosenOption(null);
            minSymbolsToSearch > 0 && setIsListOpened(false);
        }
    }, [handleCloseList, isListOpened, minSymbolsToSearch, onValueChange]);

    const handleDocumentClick = useCallback((event: Event): void => {
        if (isListOpened && !containerRef.current?.contains(event.target as Node)) {
            handleCloseList();
        }
    }, [isListOpened, handleCloseList]);

    useEffect(() => {
        document.addEventListener('keydown', handleDocumentKeyDown);
        document.addEventListener('click', handleDocumentClick);

        return () => {
            document.removeEventListener('click', handleDocumentClick);
            document.removeEventListener('keydown', handleDocumentKeyDown);
        };
    }, [handleDocumentKeyDown, handleDocumentClick]);

    // endregion

    return (
        <div
            ref={containerRef}
            className={clsx([
                className,
                interactionClass,
                'cms-autocomplete',
                clsb([style, classNames], ['container'])
            ])}
        >
            {(label || isLabelAsteriskEnabled) && (
                <Label
                    id={id}
                    label={label}
                    readOnly={readOnly}
                    infoTooltip={infoTooltip}
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                    classNames={classNames as { [cssClass: string]: string }}
                />
            )}
            <div className={clsb([style, classNames], ['innerContainer'])}>
                <input
                    id={id}
                    type="text"
                    ref={inputRef}
                    onBlur={onBlur}
                    readOnly={readOnly}
                    disabled={isDisabled}
                    onFocus={onInputFocus}
                    onChange={onInputChange}
                    tabIndex={disabled || readOnly ? -1 : 0}
                    onMouseDown={readOnly ? preventDefault : onMouseDown}
                    className={`${clsb([style, classNames], ['input'])} cms-autocomplete-input`}
                    aria-label={ariaLabel || translate('playtech.mobile.system.autocomplete.aria-label')}
                    {...placeholder && { placeholder: `${placeholder}${isPlaceholderAsteriskEnabled ? '*' : ''}` }}
                    role="combobox"
                    aria-live="polite"
                    aria-autocomplete="list"
                    aria-expanded={isListOpened}
                    {...activeValue && { 'aria-activedescendant': `${id}_${activeValue}` }}
                />

                <input
                    name={name}
                    type="hidden"
                    ref={valueInputRef}
                />

                {showClearInputIcon && !isDisabled && !readOnly && (
                    <ClearInputIcon
                        onClick={onClearInputIconClick}
                        className={classNames?.clearInputIcon}
                    />
                )}

                {isListOpened && (
                    <AutocompleteOptions
                        fieldId={id}
                        options={list}
                        Option={Option}
                        classNames={classNames}
                        activeValue={activeValue}
                        onOptionClick={onOptionClick}
                        setActiveValue={setActiveValue}
                        chosenValue={chosenOption?.value}
                        noOptionsMessage={noOptionsMessage}
                        visibleOptionsCount={visibleOptionsCount}
                        optionsContainerRef={optionsContainerRef}
                    />
                )}
            </div>

            <FieldBottomMessage
                hint={hint}
                meta={{ ...meta, ...isListOpened && { active: true }, ...customError && { error: customError } }}
                classNames={classNames as { [cssClass: string]: string }}
            />
        </div>
    );
};

export const AutocompletePure = memo(AutocompleteBare);
export const Autocomplete = withFinalFormField<string>()(AutocompletePure);

// <hub2-autocomplete name="zip" options="[{'value': '111', 'label': 'Address 1'},{'value': '222', 'label': 'Address 2'}]"></hub2-autocomplete>
componentService.registerWebContentComponents({ Autocomplete: AutocompletePure });
